import { useState, useEffect } from 'react';
import {
    FileText,
    Download,
    TrendingUp,
    TrendingDown,
    Shield,
    Clock,
    AlertTriangle,
    CheckCircle,
    BarChart3,
    PieChart,
    Settings,
    X,
    Eye,
    EyeOff,
    FileCheck,
    Ticket,
    Activity
} from 'lucide-react';

// Available metrics that users can choose from
const AVAILABLE_METRICS = [
    { id: 'totalAlerts', label: 'Total Alerts', icon: AlertTriangle, color: 'text-white' },
    { id: 'resolutionRate', label: 'Resolution Rate', icon: CheckCircle, color: 'text-green-400' },
    { id: 'mttr', label: 'Avg MTTR', icon: Clock, color: 'text-primary-400' },
    { id: 'falsePositives', label: 'False Positives', icon: FileCheck, color: 'text-yellow-400' },
    { id: 'escalationRate', label: 'Escalation Rate', icon: TrendingUp, color: 'text-orange-400' },
    { id: 'ticketsCreated', label: 'Tickets Created', icon: Ticket, color: 'text-blue-400' },
    { id: 'containmentActions', label: 'Containment Actions', icon: Shield, color: 'text-red-400' },
    { id: 'activeInvestigations', label: 'Active Investigations', icon: Activity, color: 'text-purple-400' },
];

interface ReportSummary {
    period: string;
    alertsTotal: number;
    alertsResolved: number;
    alertsEscalated: number;
    mttrMinutes: number;
    mttrChange: number;
    falsePositives: number;
    ticketsCreated: number;
    containmentActions: number;
    activeInvestigations: number;
    topSources: { name: string; count: number }[];
    topCategories: { name: string; count: number }[];
}

// Mock data for demo
const MOCK_REPORTS: ReportSummary[] = [
    {
        period: 'Today',
        alertsTotal: 42,
        alertsResolved: 34,
        alertsEscalated: 5,
        mttrMinutes: 1.8,
        mttrChange: -22,
        falsePositives: 12,
        ticketsCreated: 8,
        containmentActions: 3,
        activeInvestigations: 2,
        topSources: [
            { name: 'CrowdStrike', count: 15 },
            { name: 'Splunk', count: 12 },
            { name: 'Okta', count: 9 },
            { name: 'Sentinel', count: 6 },
        ],
        topCategories: [
            { name: 'Suspicious Login', count: 14 },
            { name: 'Malware Detection', count: 11 },
            { name: 'Policy Violation', count: 9 },
            { name: 'Data Exfiltration', count: 5 },
        ],
    },
    {
        period: 'This Week',
        alertsTotal: 247,
        alertsResolved: 193,
        alertsEscalated: 29,
        mttrMinutes: 2.3,
        mttrChange: -15,
        falsePositives: 67,
        ticketsCreated: 34,
        containmentActions: 12,
        activeInvestigations: 5,
        topSources: [
            { name: 'CrowdStrike', count: 89 },
            { name: 'Splunk', count: 67 },
            { name: 'Okta', count: 45 },
            { name: 'Sentinel', count: 46 },
        ],
        topCategories: [
            { name: 'Suspicious Login', count: 78 },
            { name: 'Malware Detection', count: 52 },
            { name: 'Policy Violation', count: 41 },
            { name: 'Data Exfiltration', count: 28 },
        ],
    },
    {
        period: 'Last Week',
        alertsTotal: 312,
        alertsResolved: 241,
        alertsEscalated: 42,
        mttrMinutes: 2.7,
        mttrChange: -8,
        falsePositives: 89,
        ticketsCreated: 51,
        containmentActions: 18,
        activeInvestigations: 0,
        topSources: [
            { name: 'Splunk', count: 102 },
            { name: 'CrowdStrike', count: 95 },
            { name: 'Sentinel', count: 61 },
            { name: 'Okta', count: 54 },
        ],
        topCategories: [
            { name: 'Suspicious Login', count: 92 },
            { name: 'Malware Detection', count: 68 },
            { name: 'Policy Violation', count: 55 },
            { name: 'Brute Force', count: 37 },
        ],
    },
];

const DEFAULT_VISIBLE_METRICS = ['totalAlerts', 'resolutionRate', 'mttr', 'falsePositives'];

export function Reports() {
    const [selectedPeriod, setSelectedPeriod] = useState<string>('Today');
    const [showCustomize, setShowCustomize] = useState(false);
    const [visibleMetrics, setVisibleMetrics] = useState<string[]>(() => {
        const saved = localStorage.getItem('zoroagent_report_metrics');
        return saved ? JSON.parse(saved) : DEFAULT_VISIBLE_METRICS;
    });

    const report = MOCK_REPORTS.find(r => r.period === selectedPeriod) || MOCK_REPORTS[0];

    const resolutionRate = Math.round((report.alertsResolved / report.alertsTotal) * 100);
    const escalationRate = Math.round((report.alertsEscalated / report.alertsTotal) * 100);

    useEffect(() => {
        localStorage.setItem('zoroagent_report_metrics', JSON.stringify(visibleMetrics));
    }, [visibleMetrics]);

    const toggleMetric = (metricId: string) => {
        setVisibleMetrics(prev => {
            if (prev.includes(metricId)) {
                return prev.filter(id => id !== metricId);
            } else if (prev.length < 4) {
                return [...prev, metricId];
            }
            return prev;
        });
    };

    const getMetricValue = (metricId: string) => {
        switch (metricId) {
            case 'totalAlerts': return report.alertsTotal;
            case 'resolutionRate': return `${resolutionRate}%`;
            case 'mttr': return `${report.mttrMinutes}m`;
            case 'falsePositives': return report.falsePositives;
            case 'escalationRate': return `${escalationRate}%`;
            case 'ticketsCreated': return report.ticketsCreated;
            case 'containmentActions': return report.containmentActions;
            case 'activeInvestigations': return report.activeInvestigations;
            default: return 0;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/25">
                            <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Security Reports</h2>
                            <p className="text-sm text-slate-400">Agent performance & threat analytics</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Period Selector */}
                        <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
                            {['Today', 'This Week', 'Last Week'].map(period => (
                                <button
                                    key={period}
                                    onClick={() => setSelectedPeriod(period)}
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedPeriod === period
                                        ? 'bg-primary-500 text-white'
                                        : 'text-slate-400 hover:text-white'
                                        }`}
                                >
                                    {period}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setShowCustomize(true)}
                            className="btn btn-ghost p-2"
                            title="Customize Metrics"
                        >
                            <Settings className="w-5 h-5" />
                        </button>
                        <button className="btn btn-primary flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            Export
                        </button>
                    </div>
                </div>

                {/* Key Metrics - Dynamic based on user selection */}
                <div className={`grid gap-4 ${visibleMetrics.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                    {visibleMetrics.map(metricId => {
                        const metric = AVAILABLE_METRICS.find(m => m.id === metricId);
                        if (!metric) return null;
                        const Icon = metric.icon;
                        const value = getMetricValue(metricId);

                        return (
                            <div key={metricId} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                                <div className="flex items-center gap-2 mb-2">
                                    <Icon className={`w-4 h-4 ${metric.color}`} />
                                    <span className="text-xs text-slate-400">{metric.label}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-2xl font-bold ${metric.color}`}>{value}</span>
                                    {metricId === 'mttr' && (
                                        <span className={`text-xs flex items-center gap-1 ${report.mttrChange < 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {report.mttrChange < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                            {Math.abs(report.mttrChange)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Customize Modal */}
            {showCustomize && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-[98]" onClick={() => setShowCustomize(false)} />
                    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-[99] p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white">Customize Report</h3>
                            <button onClick={() => setShowCustomize(false)} className="p-1 hover:bg-slate-800 rounded">
                                <X className="w-5 h-5 text-slate-400" />
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mb-4">Select up to 4 metrics to display in your report.</p>
                        <div className="space-y-2">
                            {AVAILABLE_METRICS.map(metric => {
                                const Icon = metric.icon;
                                const isVisible = visibleMetrics.includes(metric.id);
                                const isDisabled = !isVisible && visibleMetrics.length >= 4;

                                return (
                                    <button
                                        key={metric.id}
                                        onClick={() => toggleMetric(metric.id)}
                                        disabled={isDisabled}
                                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${isVisible
                                            ? 'bg-primary-500/20 border border-primary-500/30'
                                            : isDisabled
                                                ? 'bg-slate-800/30 opacity-50 cursor-not-allowed'
                                                : 'bg-slate-800/50 hover:bg-slate-800 border border-transparent'
                                            }`}
                                    >
                                        <Icon className={`w-4 h-4 ${metric.color}`} />
                                        <span className="text-sm text-white flex-1 text-left">{metric.label}</span>
                                        {isVisible ? (
                                            <Eye className="w-4 h-4 text-primary-400" />
                                        ) : (
                                            <EyeOff className="w-4 h-4 text-slate-500" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-700">
                            <button
                                onClick={() => {
                                    setVisibleMetrics(DEFAULT_VISIBLE_METRICS);
                                }}
                                className="text-xs text-slate-500 hover:text-slate-300"
                            >
                                Reset to defaults
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-2 gap-6">
                {/* Alert Sources */}
                <div className="glass rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <PieChart className="w-5 h-5 text-primary-400" />
                        <h3 className="text-lg font-semibold text-white">Alerts by Source</h3>
                    </div>
                    <div className="space-y-3">
                        {report.topSources.map((source, idx) => {
                            const percentage = Math.round((source.count / report.alertsTotal) * 100);
                            const colors = ['bg-primary-500', 'bg-blue-500', 'bg-purple-500', 'bg-cyan-500'];
                            return (
                                <div key={source.name}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-slate-300">{source.name}</span>
                                        <span className="text-slate-400">{source.count} ({percentage}%)</span>
                                    </div>
                                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full ${colors[idx]} rounded-full transition-all`}
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Alert Categories */}
                <div className="glass rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-5 h-5 text-primary-400" />
                        <h3 className="text-lg font-semibold text-white">Top Threat Categories</h3>
                    </div>
                    <div className="space-y-3">
                        {report.topCategories.map((cat, idx) => {
                            const maxCount = Math.max(...report.topCategories.map(c => c.count));
                            const percentage = Math.round((cat.count / maxCount) * 100);
                            const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];
                            return (
                                <div key={cat.name}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-slate-300">{cat.name}</span>
                                        <span className="text-slate-400">{cat.count}</span>
                                    </div>
                                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full ${colors[idx]} rounded-full transition-all`}
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Resolution Breakdown */}
            <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-primary-400" />
                    <h3 className="text-lg font-semibold text-white">Resolution Breakdown</h3>
                </div>
                <div className="grid grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-green-500/10 rounded-xl border border-green-500/20">
                        <div className="text-4xl font-bold text-green-400 mb-2">{report.alertsResolved}</div>
                        <div className="text-sm text-slate-400">Auto-Resolved</div>
                        <div className="text-xs text-green-400 mt-1">{resolutionRate}% of total</div>
                    </div>
                    <div className="text-center p-6 bg-orange-500/10 rounded-xl border border-orange-500/20">
                        <div className="text-4xl font-bold text-orange-400 mb-2">{report.alertsEscalated}</div>
                        <div className="text-sm text-slate-400">Escalated</div>
                        <div className="text-xs text-orange-400 mt-1">{escalationRate}% of total</div>
                    </div>
                    <div className="text-center p-6 bg-slate-500/10 rounded-xl border border-slate-500/20">
                        <div className="text-4xl font-bold text-slate-400 mb-2">{report.alertsTotal - report.alertsResolved - report.alertsEscalated}</div>
                        <div className="text-sm text-slate-400">Pending Review</div>
                        <div className="text-xs text-slate-500 mt-1">{100 - resolutionRate - escalationRate}% of total</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
